import { ChangeEvent } from "react"
import {
  FortesDropdown,
  FortesNumberFieldBasic,
} from "../FortesInputs/FortesFields"
import { Box } from "@mui/material"
import SpaceSettingPreview from "../SpaceSettingsPreview"
import DrawerHeader from "./DrawerHeader"
import { Currency, Foil } from "../../common/Models"
import { FortesSwitch } from "../FortesInputs/FortesSwitch"
import { useAdminContext } from "../../AdminContext"
import { scrollbarSX } from "../FortesInputs/FortesScrollbar"

export default function SpaceDrawer(props: {
  docW: number
  handleDocWChange: (newVal: number) => void
  spacing: number
  handleSpacingChange: (newVal: number) => void
  docBorder: number
  handleDocBorderChange: (newVal: number) => void
  pricePerM2: number
  handlePriceChange: (newVal: number) => void
  currency: Currency
  handleCurrencyChange: (newCurrency: Currency) => void
  onClose: () => void
  onReload: () => void
  drawerHeight: number
  foil: Foil
  handleFoilChange: (newFoil: Foil) => void
  machineGripMM: number
  handleMachineGripMMChange: (newVal: number) => void
  plotrPrice: [number, number, number]
  handlePlotrPriceChange: (newVal: [number, number, number]) => void
  marginDivisor: number
  handleMarginDivisorChange: (newVal: number) => void
}): JSX.Element {
  const { isAdmin } = useAdminContext()

  return (
    <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between">
      <Box>
        <DrawerHeader
          title="Foil measurements"
          onClose={props.onClose}
          onReload={props.onReload}
        />
        <Box
          sx={{
            overflow: "auto",
            ...scrollbarSX,
            maxHeight: `${props.drawerHeight - 305 - 62}px`,
          }}
        >
          {isAdmin && (
            <Box
              sx={{
                margin: "0px 20px 0px 20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FortesSwitch
                options={[
                  {
                    name: "LOTUS Foil",
                    onClick: () => {
                      props.handleFoilChange("LOTUS Foil")
                    },
                  },
                  {
                    name: "custom foil",
                    onClick: () => {
                      props.handleFoilChange("custom foil")
                    },
                  },
                ]}
                value={props.foil}
                width="Medium"
              />
            </Box>
          )}

          <Box sx={{ margin: "20px 20px 0px 20px" }}>
            <FortesNumberFieldBasic
              label={"Foil width (mm)"}
              initalValue={props.docW}
              width="Large"
              enabled={props.foil !== "LOTUS Foil" || isAdmin}
              onChange={(
                e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => props.handleDocWChange(Number(e.target.value))}
            />
          </Box>

          <Box sx={{ margin: "20px 20px 0px 20px" }}>
            <FortesNumberFieldBasic
              label={"Spaces between cut-outs (mm)"}
              initalValue={props.spacing}
              width="Large"
              onChange={(
                e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => props.handleSpacingChange(Number(e.target.value))}
            />
          </Box>

          <Box sx={{ margin: "20px 20px 0px 20px" }}>
            <FortesNumberFieldBasic
              label={"Spaces around border (mm)"}
              initalValue={props.docBorder}
              width="Large"
              onChange={(
                e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => props.handleDocBorderChange(Number(e.target.value))}
            />
          </Box>

          {(isAdmin || props.foil !== "LOTUS Foil") && (
            <Box
              sx={{ margin: "20px 20px 0px 20px" }}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <FortesNumberFieldBasic
                label={"Base price (for 1 m²)"}
                initalValue={props.pricePerM2}
                width="Medium"
                onChange={(
                  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => props.handlePriceChange(Number(e.target.value))}
              />
              <FortesDropdown<Currency>
                label={"Currency"}
                initialValue={props.currency}
                width="Small"
                onChange={props.handleCurrencyChange}
                options={["CZK", "EUR"]}
                stringToT={(toConvert: string) => {
                  if (toConvert === "CZK" || toConvert === "EUR") return toConvert
                  else return undefined
                }}
                TToString={(toConvert: Currency) => toConvert}
              />
            </Box>
          )}

          {isAdmin && (
            <Box sx={{ margin: "20px 20px 0px 20px" }}>
              <FortesNumberFieldBasic
                label="Free machine grip margin (mm)"
                initalValue={props.machineGripMM}
                width="Large"
                onChange={(e) =>
                  props.handleMachineGripMMChange(Number(e.target.value))
                }
              />
            </Box>
          )}

          {isAdmin && (
            <Box sx={{ margin: "20px 20px 0px 20px" }}>
              <FortesNumberFieldBasic
                label="Price margin divisor"
                initalValue={props.marginDivisor}
                width="Large"
                onChange={(e) =>
                  props.handleMarginDivisorChange(Number(e.target.value))
                }
              />
            </Box>
          )}

          {isAdmin && (
            <Box
              sx={{ margin: "20px 20px 0px 20px" }}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
            >
              <FortesNumberFieldBasic
                label={`Plotr price 5-14"`}
                initalValue={props.plotrPrice[0]}
                width="Small"
                onChange={(e) =>
                  props.handlePlotrPriceChange([
                    Number(e.target.value),
                    props.plotrPrice[1],
                    props.plotrPrice[2],
                  ])
                }
              />
              <FortesNumberFieldBasic
                label={`15-22"`}
                initalValue={props.plotrPrice[1]}
                width="Small"
                onChange={(e) =>
                  props.handlePlotrPriceChange([
                    props.plotrPrice[0],
                    Number(e.target.value),
                    props.plotrPrice[2],
                  ])
                }
              />
              <FortesNumberFieldBasic
                label={`23-32"`}
                initalValue={props.plotrPrice[2]}
                width="Small"
                onChange={(e) =>
                  props.handlePlotrPriceChange([
                    props.plotrPrice[0],
                    props.plotrPrice[1],
                    Number(e.target.value),
                  ])
                }
              />
            </Box>
          )}
          <Box sx={{ height: "20px" }} />
        </Box>
      </Box>
      <Box>
        <SpaceSettingPreview
          heightAvailable={props.drawerHeight - 405 - 62 - 40} // 405: form h, 62: header h, 40: white space
          docWidth={props.docW}
          docBorder={props.docBorder}
          spacing={props.spacing}
        />
      </Box>
    </Box>
  )
}
